import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Plus, Search, FolderKanban, Users, Loader2, CalendarDays,
  CheckCircle2, CircleDot, Ban, Clock, Zap,
} from "lucide-react";
import { CreateProjectWizard } from "@/components/CreateProjectWizard";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const statusStyles = {
  active: { badge: "bg-emerald-50 text-emerald-600 border-emerald-200", dot: "bg-emerald-500" },
  in_review: { badge: "bg-amber-50 text-amber-600 border-amber-200", dot: "bg-amber-400" },
  blocked: { badge: "bg-red-50 text-red-600 border-red-200", dot: "bg-red-500" },
  completed: { badge: "bg-[#014d7d]/8 text-[#014d7d] border-[#014d7d]/20", dot: "bg-[#014d7d]" },
};

export const ProjectList = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const filtered = projects?.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.title?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-foreground/45" /></div>;

  return (
    <div className="flex flex-col h-full" data-testid="project-list-page">
      <div className="px-6 pt-5 pb-4 bg-white border-b border-border/40">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-lg text-foreground">Projects</h2>
          <button onClick={() => setWizardOpen(true)} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#014d7d] text-white flex items-center gap-1.5" data-testid="new-project-btn">
            <Plus className="w-3.5 h-3.5" /> New Project
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/45" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search projects..."
            className="w-full max-w-sm h-10 pl-10 pr-4 text-sm border border-border/60 rounded-xl bg-white outline-none focus:ring-2 focus:ring-[#014d7d]/10 placeholder:text-foreground/45"
            data-testid="project-search" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="project-grid">
          {filtered?.map(p => {
            const name = p.name || p.title || "Untitled";
            const st = statusStyles[p.status] || statusStyles.active;
            const stages = p.workflow_stages || [];
            const team = p.team || [];
            return (
              <Link key={p.id} to={`/projects/${p.id}/detail`} className="group bg-white border border-border/60 rounded-2xl overflow-hidden hover:shadow-soft" data-testid={`project-card-${p.id}`}>
                {/* Progress bar header */}
                <div className="h-1.5 bg-gray-100">
                  <div className="h-full bg-gradient-to-r from-[#6baed6] to-[#178a55]" style={{ width: `${p.progress || 0}%` }} />
                </div>

                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-sm font-bold text-foreground group-hover:text-[#014d7d]">{name}</h3>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize shrink-0 ml-2 ${st.badge}`}>
                      {(p.status || "active").replace("_", " ")}
                    </span>
                  </div>
                  <p className="text-xs text-foreground/40 leading-relaxed mb-3 line-clamp-2">{p.description}</p>

                  {/* Workflow stage dots */}
                  {stages.length > 0 && (
                    <div className="flex items-center gap-1 mb-3">
                      {stages?.map((s, i) => (
                        <div key={i} className="flex items-center gap-1 flex-1">
                          <div className={`h-1.5 flex-1 rounded-full ${s.status === "completed" ? "bg-emerald-500" : s.status === "in_progress" ? "bg-[#014d7d]" : "bg-gray-200"}`} />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-[10px] text-foreground/40">
                    <div className="flex items-center gap-3">
                      {team.length > 0 && (
                        <span className="flex items-center gap-1">
                          <div className="flex -space-x-1.5">
                            {team.slice(0, 3)?.map((m, i) => (
                              <div key={i} className="w-5 h-5 rounded-full border border-white flex items-center justify-center text-white text-[7px] font-bold" style={{ background: m.color }}>{m.initials}</div>
                            ))}
                          </div>
                          {team.length > 3 && <span>+{team.length - 3}</span>}
                        </span>
                      )}
                      {p.due_date && <span className="flex items-center gap-0.5"><CalendarDays className="w-3 h-3" /> {new Date(p.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                    </div>
                    <span className="font-semibold">{p.progress || 0}%</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      <CreateProjectWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        prefillGoal=""
        prefillWorkflowType=""
      />
    </div>
  );
};
